import { angleDiffDeg, normalizeDeg } from './geo.ts';

/*
 * Critically damped spring smoothing for on-screen readouts and map cameras. Closed-form (exact for any
 * dt, so a 100 ms frame hitch cannot blow it up) and velocity-carrying: retargeting mid-flight keeps the
 * current velocity, so motion never kinks. Pure and stateless; the caller owns the state.
 */

export interface SpringState {
  value: number;
  velocity: number;
}

export interface SpringOptions {
  /** Time (s) of one undamped oscillation; smaller = snappier. 0 snaps. Default 0.4. */
  response?: number;
  /** Damping ratio: 1 = critical (no overshoot), < 1 bouncy, > 1 sluggish. Default 1. */
  damping?: number;
}

export const SPRING_DEFAULT_RESPONSE_SEC = 0.4;
export const SPRING_DEFAULT_DAMPING = 1;

/** Offset `x` from the target and its velocity `v`, advanced `dt` seconds. */
function advance(x: number, v: number, dt: number, w: number, z: number): { x: number; v: number } {
  if (Math.abs(z - 1) < 1e-6) {
    const e = Math.exp(-w * dt);
    const c2 = v + w * x;
    return { x: (x + c2 * dt) * e, v: (v - w * c2 * dt) * e };
  }
  if (z < 1) {
    const wd = w * Math.sqrt(1 - z * z);
    const e = Math.exp(-z * w * dt);
    const cos = Math.cos(wd * dt);
    const sin = Math.sin(wd * dt);
    const b = (v + z * w * x) / wd;
    return { x: e * (x * cos + b * sin), v: e * (v * cos - (x * wd + (z * w / wd) * (v + z * w * x)) * sin) };
  }
  const s = w * Math.sqrt(z * z - 1);
  const r1 = -z * w + s;
  const r2 = -z * w - s;
  const a = (v - r2 * x) / (r1 - r2);
  const b = x - a;
  const e1 = Math.exp(r1 * dt);
  const e2 = Math.exp(r2 * dt);
  return { x: a * e1 + b * e2, v: a * r1 * e1 + b * r2 * e2 };
}

function step(offset: number, velocity: number, dt: number, opts: SpringOptions): { x: number; v: number } | null {
  const response = opts.response ?? SPRING_DEFAULT_RESPONSE_SEC;
  const damping = opts.damping ?? SPRING_DEFAULT_DAMPING;
  if (!(response > 0) || !(damping > 0)) return null; // snap
  if (!(dt > 0) || !Number.isFinite(dt)) return { x: offset, v: velocity };
  return advance(offset, velocity, dt, (2 * Math.PI) / response, damping);
}

/** Advance `state` toward `target` by `dt` seconds. Response ≤ 0 snaps to the target with zero velocity. */
export function stepSpring(state: SpringState, target: number, dt: number, opts: SpringOptions = {}): SpringState {
  if (!Number.isFinite(target)) return state;
  const r = step(state.value - target, state.velocity, dt, opts);
  return r ? { value: target + r.x, velocity: r.v } : { value: target, velocity: 0 };
}

/**
 * Same for an angle in degrees: always the shortest way round (350° → 10° is +20°, not −340°). The
 * value stays in [0, 360); velocity is °/s, signed.
 */
export function stepAngleSpring(state: SpringState, target: number, dt: number, opts: SpringOptions = {}): SpringState {
  if (!Number.isFinite(target)) return state;
  const r = step(-angleDiffDeg(state.value, target), state.velocity, dt, opts);
  return r ? { value: normalizeDeg(target + r.x), velocity: r.v } : { value: normalizeDeg(target), velocity: 0 };
}
